/**
 * API service for list search functionality.
 * Handles location search and filter options fetching.
 */

import { getApiBaseUrl, getApiVersion } from '../../../shared/api/runtimeConfig'
import type {
  PaginatedSearchResponse,
  FilterOptionsResponse,
  ListSearchParams,
  LocationFilters,
  LocationType,
} from '../types'
import i18n from '../../../i18n'

function getSearchBaseUrl(): string {
  return `${getApiBaseUrl()}/${getApiVersion()}/locations`
}

export class HttpError extends Error {
  status: number
  data?: unknown

  constructor(message: string, status: number, data?: unknown) {
    super(message)
    this.name = 'HttpError'
    this.status = status
    this.data = data
  }
}

function extractErrorMessage(errorData: unknown): string | null {
  if (!errorData || typeof errorData !== 'object') return null

  const anyData = errorData as Record<string, unknown>

  if (typeof anyData.detail === 'string' && anyData.detail.trim()) {
    return anyData.detail
  }

  if (typeof anyData.message === 'string' && anyData.message.trim()) {
    return anyData.message
  }

  // DRF field errors
  for (const value of Object.values(anyData)) {
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'string') {
      return value.filter((v) => typeof v === 'string').join(', ')
    }
  }

  return null
}

/**
 * Maps location type from URL to API location_types parameter
 */
export function mapLocationTypeToApi(locationType: LocationType): string {
  const mapping: Record<LocationType, string> = {
    refuges: 'refuge',
    campings: 'camping',
    organisations: 'organization',
  }
  return mapping[locationType]
}

/**
 * Search locations with filters and pagination
 *
 * @param params - Search parameters
 * @param signal - Optional abort signal
 * @returns Promise with paginated search results
 */
export async function searchLocations(
  params: ListSearchParams,
  signal?: AbortSignal,
): Promise<PaginatedSearchResponse> {
  const baseUrl = `${getSearchBaseUrl()}/search/`
  const queryParams = new URLSearchParams()

  // Search query - API now supports optional q parameter
  // If query is provided and has at least 2 characters, include it
  // Otherwise, omit the parameter to get all locations matching filters
  if (params.query && params.query.trim().length >= 2) {
    queryParams.set('q', params.query.trim())
  }

  // Location type filter - DRF ListField expects repeated query params
  // Use append to ensure proper parsing even for a single value
  const apiLocationType = mapLocationTypeToApi(params.locationType)
  queryParams.append('location_types', apiLocationType)

  if (params.filters) appendLocationFilterParams(queryParams, params.filters)

  // Pagination
  if (params.cursor) {
    queryParams.set('cursor', params.cursor)
  }
  queryParams.set('page_size', (params.page_size || 12).toString())

  if (params.has_photos === true) {
    queryParams.set('has_photos', 'true')
  }
  if (params.random === true) {
    queryParams.set('random', 'true')
  }

  const url = `${baseUrl}?${queryParams.toString()}`
  const response = await fetch(url, { signal })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)

    throw new HttpError(
      msg || i18n.t('list-search:listSearchApi.failed_to_search_locations', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }

  const data: PaginatedSearchResponse = await response.json()
  return data
}

/** Filtres de liste → paramètres de `/locations/search/` (aussi utilisé par l'app mobile). */
export function appendLocationFilterParams(
  queryParams: URLSearchParams,
  filters: LocationFilters,
): void {
  if (filters.country_id) {
    queryParams.set('country_id', filters.country_id.toString())
  }
  if (filters.province_id) {
    queryParams.set('province_id', filters.province_id.toString())
  }
  if (filters.activity_id) {
    queryParams.set('activity_id', filters.activity_id.toString())
  }
  if (filters.organization_id) {
    queryParams.set('organization_id', filters.organization_id.toString())
  }
  if (filters.season) {
    queryParams.set('season', filters.season)
  }
  // Type-specific filters
  if (filters.building_type_id) {
    queryParams.set('building_type_id', filters.building_type_id.toString())
  }
  if (filters.camping_type_id) {
    queryParams.set('camping_type_id', filters.camping_type_id.toString())
  }
  if (filters.organization_type_id) {
    queryParams.set('organization_type_id', filters.organization_type_id.toString())
  }
  for (const key of [
    'building_type_ids',
    'camping_type_ids',
    'organization_type_ids',
    'contains_poi_type_ids',
  ] as const) {
    filters[key]?.forEach((id) => queryParams.append(key, id.toString()))
  }
  filters.contains_location_types?.forEach((v) =>
    queryParams.append('contains_location_types', v),
  )
  
  // Refuge-specific filters (API expects strings: 'yes'/'no' for booleans)
  if (filters.dogs_allowed) {
    queryParams.set('dogs_allowed', filters.dogs_allowed)
  }
  if (filters.car_access) {
    queryParams.set('car_access', filters.car_access)
  }
  if (filters.drinking_water) {
    queryParams.set('drinking_water', filters.drinking_water)
  }
  if (filters.toilet) {
    queryParams.set('toilet', filters.toilet)
  }
  if (filters.capacity_min) {
    queryParams.set('capacity_min', filters.capacity_min.toString())
  }
  if (filters.capacity_max) {
    queryParams.set('capacity_max', filters.capacity_max.toString())
  }
  if (filters.rental_type) {
    queryParams.set('rental_type', filters.rental_type)
  }
  if (filters.exclusive_occupation) {
    queryParams.set('exclusive_occupation', filters.exclusive_occupation)
  }
  
  // Camping-specific filters
  if (filters.dog_friendly) {
    queryParams.set('dog_friendly', filters.dog_friendly)
  }
  if (filters.showers) {
    queryParams.set('showers', filters.showers)
  }
  if (filters.welcome_cyclist) {
    queryParams.set('welcome_cyclist', filters.welcome_cyclist)
  }
  if (filters.tent_only) {
    queryParams.set('tent_only', filters.tent_only)
  }
  if (filters.verified !== null && filters.verified !== undefined) {
    queryParams.set('verified', filters.verified.toString())
  }
  if (filters.placement_type_ids && filters.placement_type_ids.length > 0) {
    // DRF ListField expects repeated query params - append each ID
    filters.placement_type_ids.forEach((id) => {
      queryParams.append('placement_type_ids', id.toString())
    })
  }
  if (filters.activity_ids && filters.activity_ids.length > 0) {
    filters.activity_ids.forEach((id) => {
      queryParams.append('activity_ids', id.toString())
    })
  }
}

/**
 * Fetch filter options for all location types
 *
 * @param signal - Optional abort signal
 * @returns Promise with filter options
 */
export async function fetchFilterOptions(signal?: AbortSignal): Promise<FilterOptionsResponse> {
  const url = `${getSearchBaseUrl()}/filter-options/`
  const response = await fetch(url, { signal })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)

    throw new HttpError(
      msg || i18n.t('list-search:listSearchApi.failed_to_fetch_filter_options', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }

  const data: FilterOptionsResponse = await response.json()
  return data
}

/**
 * Geographic hierarchy types
 */
export interface Country {
  id: number
  name: string
  code?: string
}

export interface Province {
  id: number
  name: string
  code?: string
  country_id: number
}

export interface Organization {
  id: number
  name: string
  country_id: number | null
  province_id: number | null
}

export interface Activity {
  id: number
  name: string
  season?: string
  organization_id: number | null
}

/**
 * Fetch countries from geographic hierarchy
 */
export async function fetchCountries(signal?: AbortSignal): Promise<Country[]> {
  const url = `${getSearchBaseUrl()}/hierarchy/countries/`
  const response = await fetch(url, { signal })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)

    throw new HttpError(
      msg || i18n.t('list-search:listSearchApi.failed_to_fetch_countries', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }

  return response.json()
}

/**
 * Fetch provinces from geographic hierarchy, optionally filtered by country
 */
export async function fetchProvinces(
  options: { country_id?: number; signal?: AbortSignal } = {},
): Promise<Province[]> {
  const url = `${getSearchBaseUrl()}/hierarchy/provinces/`
  const params = new URLSearchParams()
  if (options.country_id) {
    params.append('country_id', options.country_id.toString())
  }
  const fullUrl = params.toString() ? `${url}?${params.toString()}` : url
  const response = await fetch(fullUrl, { signal: options.signal })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)

    throw new HttpError(
      msg || i18n.t('list-search:listSearchApi.failed_to_fetch_provinces', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }

  return response.json()
}

/**
 * Fetch organizations from geographic hierarchy, optionally filtered by country, province, or location type
 */
export async function fetchOrganizations(
  options: { country_id?: number; province_id?: number; location_type?: string; signal?: AbortSignal } = {},
): Promise<Organization[]> {
  const url = `${getSearchBaseUrl()}/hierarchy/organizations/`
  const params = new URLSearchParams()
  if (options.country_id) {
    params.append('country_id', options.country_id.toString())
  }
  if (options.province_id) {
    params.append('province_id', options.province_id.toString())
  }
  if (options.location_type) {
    params.append('location_type', options.location_type)
  }
  const fullUrl = params.toString() ? `${url}?${params.toString()}` : url
  const response = await fetch(fullUrl, { signal: options.signal })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)

    throw new HttpError(
      msg || i18n.t('list-search:listSearchApi.failed_to_fetch_organizations', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }

  return response.json()
}

/**
 * Fetch activities from geographic hierarchy, optionally filtered by organization
 */
export async function fetchActivities(
  options: { organization_id?: number; all?: boolean; signal?: AbortSignal } = {},
): Promise<Activity[]> {
  const url = `${getSearchBaseUrl()}/hierarchy/activities/`
  const params = new URLSearchParams()
  if (options.organization_id) {
    params.append('organization_id', options.organization_id.toString())
  }
  if (options.all) {
    params.append('all', 'true')
  }
  const fullUrl = params.toString() ? `${url}?${params.toString()}` : url
  const response = await fetch(fullUrl, { signal: options.signal })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const msg = extractErrorMessage(errorData)

    throw new HttpError(
      msg || i18n.t('list-search:listSearchApi.failed_to_fetch_activities', { status: response.status, statusText: response.statusText }),
      response.status,
      errorData,
    )
  }

  return response.json()
}